/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React from "react"
import { Select } from "@duxton-web/ui"
import { useSelector } from "react-redux"
import { compact, isEmpty, uniq } from "lodash"

import { AppState } from "store/store.type"
import { ServiceType } from "constants/feedback"
import { BusinessType } from "types/models/menu"
import { ExtraReviewsFilterConfig } from "types/api/menuFeedback.type"
import { useTranslationsMap } from "common/utils/i18n"
import { isFoodStore, isMartStore } from "utils/menu_store_helper"
import { getFeedbackDateRange } from "utils/feedback_helper"
import { filterOutlets } from "./utils"

export interface FeedbackFiltersProps {
  onFetchReviews?(
    extras?: ExtraReviewsFilterConfig & { merchantIDs?: string[] },
  ): any
  isSingleStore?: boolean
  singleStoreID?: string
}

const FeedbackTopFilters: React.FC<FeedbackFiltersProps> = ({
  onFetchReviews = () => {},
  isSingleStore = false,
  singleStoreID,
}) => {
  const {
    menu: { outlets },
    feedback: { filterState },
  } = useSelector((state: AppState) => state)
  const cities = uniq(outlets?.map(o => o.city))
  const textMap = useTranslationsMap({
    city: "menu.stores.table.header.city", // borrow content
    delivery: "feedback.filters.serviceType.delivery",
    deliveryAndSelfPickup: "feedback.filters.serviceType.deliveryAndSelfPickup",
    grabFood: "feedback.filters.businessType.grabFood",
    grabMart: "feedback.filters.businessType.grabMart",
    dineIn: "feedback.filters.serviceType.dineIn",
    allStores: "feedback.filters.allStores",
  })

  const hasGrabFood = Array.isArray(outlets) && outlets?.some(isFoodStore)
  const hasGrabMart = Array.isArray(outlets) && outlets?.some(isMartStore)

  const businessTypeEnableOption = {
    [BusinessType.Food]: hasGrabFood,
    [BusinessType.Shop]: hasGrabMart,
  }

  const businessTypeText: { [key: string]: string } = {
    [BusinessType.Food]: textMap.grabFood,
    [BusinessType.Shop]: textMap.grabMart,
  }

  // initialize these values as true in case the outlets array is not available
  let hasDelivery = true
  let hasSelfPickup = true
  let hasDineIn = true
  if (Array.isArray(outlets) && !isEmpty(outlets)) {
    // single store users should only read their respective merchantID
    const filteredOutlets =
      (isSingleStore && singleStoreID
        ? outlets?.filter(o => o.merchantID === singleStoreID)
        : outlets) || []

    hasDelivery = filteredOutlets.some(o =>
      o.deliverOption?.includes("DELIVERY"),
    )
    hasSelfPickup = filteredOutlets.some(o =>
      o.deliverOption?.includes("TAKEAWAY"),
    )
    hasDineIn = filteredOutlets.some(o => o.deliverOption?.includes("DINEIN"))
  }

  const serviceTypeText: { [key: string]: string } = {
    [ServiceType.DELIVERY]: hasSelfPickup
      ? textMap.deliveryAndSelfPickup
      : textMap.delivery,
    [ServiceType.DINEIN]: textMap.dineIn,
  }

  const handleOutletChange = (value?: string[]) => {
    let merchantIDs = value
    if (
      (value?.includes("ALL") || isEmpty(value)) &&
      Array.isArray(outlets) &&
      outlets?.length > 0
    ) {
      merchantIDs = undefined
    }

    onFetchReviews({
      merchantIDs,
    })
  }

  const handleServiceTypeChange = (serviceType: ServiceType) => {
    const { startDate, endDate } = getFeedbackDateRange()

    onFetchReviews({
      serviceType,
      merchantIDs: undefined,
      // clear date filter since there may be a difference in max allowed date limit between service types
      startDate,
      endDate,
    })
  }

  const handleBusinessTypeChange = (businessType: BusinessType) => {
    onFetchReviews({
      businessType,
      merchantIDs: undefined,
    })
  }

  const handleCityChange = (cityName?: string) => {
    onFetchReviews({
      cityName,
    })
  }

  const filteredOutlets = filterOutlets({
    outlets: outlets || [],
    cityName: filterState?.cityName,
    businessType: filterState?.businessType,
    serviceType: filterState?.serviceType,
  })

  const availableServiceTypes = compact([
    (hasDelivery || hasDineIn) && ServiceType.DELIVERY,
    hasDineIn && ServiceType.DINEIN,
  ])

  const showServiceTypes =
    filterState?.businessType !== BusinessType.Shop &&
    !!availableServiceTypes.length

  return (
    <div
      css={css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "16px",
      })}
    >
      {!isSingleStore && (
        <Select
          style={{
            minWidth: 160,
          }}
          placeholder={textMap.city}
          onChange={handleCityChange}
          data-testid="feedback-city-selector"
          value={filterState?.cityName}
        >
          {cities?.map(c => (
            <Select.Option key={c} value={c}>
              {c}
            </Select.Option>
          ))}
        </Select>
      )}

      {!isSingleStore && (
        <Select
          style={{
            minWidth: 160,
          }}
          onChange={handleBusinessTypeChange}
          data-testid="feedback-business-type-selector"
          value={filterState?.businessType ?? BusinessType.Food}
        >
          {[BusinessType.Food, BusinessType.Shop].map(bt => (
            <Select.Option
              value={bt}
              key={bt}
              disabled={!businessTypeEnableOption[bt]}
            >
              {businessTypeText[bt]}
            </Select.Option>
          ))}
        </Select>
      )}

      {showServiceTypes && (
        <Select
          style={{
            minWidth: 150,
          }}
          onChange={handleServiceTypeChange}
          data-testid="feedback-service-type-selector"
          value={filterState?.serviceType ?? ServiceType.DELIVERY}
        >
          {availableServiceTypes.map(st => (
            <Select.Option value={st} key={st}>
              {serviceTypeText[st]}
            </Select.Option>
          ))}
        </Select>
      )}

      {!isSingleStore && (
        <Select
          style={{
            minWidth: 200,
          }}
          placeholder={textMap.allStores}
          mode="multiple"
          onChange={handleOutletChange}
          allowClear
          data-testid="feedback-outlet-type-selector"
          value={filterState?.merchantIDs}
        >
          <Select.Option key="ALL-OUTLET" value="ALL">
            {textMap.allStores}
          </Select.Option>
          {filteredOutlets.map(outlet => (
            <Select.Option key={outlet.merchantID} value={outlet.merchantID}>
              {outlet.merchantName}
            </Select.Option>
          ))}
        </Select>
      )}
    </div>
  )
}

export default FeedbackTopFilters
